import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Grid, Label } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import {
  API,
  downloadTextAsFile,
  showError,
  showSuccess,
} from '../../helpers';
import RulesTable from './RulesTable';
import EditRuleModal from './EditRuleModal';

const ConcurrencyConfigPage = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState({
    version: '1.0',
    enabled: false,
    limits: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputRef = useRef(null);

  const summary = useMemo(() => {
    const limits = config.limits || [];
    const p1 = limits.filter(
      (r) => r.channel_pattern && r.model && r.task_type
    ).length;
    const p2 = limits.filter(
      (r) => !r.channel_pattern && r.model && !r.task_type
    ).length;
    const p3 = limits.filter(
      (r) => r.channel_pattern && !r.model && r.task_type
    ).length;
    const p4 = limits.filter(
      (r) => !r.channel_pattern && !r.model && r.task_type
    ).length;
    return { total: limits.length, p1, p2, p3, p4 };
  }, [config.limits]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/concurrency/config');
      const { success, message, data } = res.data;
      if (success) {
        setConfig(data || { version: '1.0', enabled: false, limits: [] });
      } else {
        showError(message || t('concurrency.messages.load_failed'));
      }
    } catch (error) {
      showError(error.message || t('concurrency.messages.load_failed'));
    }
    setLoading(false);
  }, [t]);

  useEffect(() => {
    loadConfig().then();
  }, [loadConfig]);

  const updateMemoryConfig = async (newConfig) => {
    try {
      const res = await API.put('/api/concurrency/config', {
        config: JSON.stringify(newConfig),
      });
      const { success, message } = res.data;
      if (!success) {
        showError(message || t('concurrency.messages.update_failed'));
        return false;
      }
      setConfig(newConfig);
      return true;
    } catch (error) {
      showError(error.message || t('concurrency.messages.update_failed'));
      return false;
    }
  };

  const handleToggleEnabled = async () => {
    const newConfig = { ...config, enabled: !config.enabled };
    await updateMemoryConfig(newConfig);
  };

  const openCreateModal = () => {
    setEditingIndex(-1);
    setModalOpen(true);
  };

  const openEditModal = (index) => {
    setEditingIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingIndex(-1);
  };

  const handleSave = async (rule) => {
    setSaving(true);
    try {
      const newLimits = [...(config.limits || [])];
      if (editingIndex >= 0) {
        newLimits[editingIndex] = rule;
      } else {
        newLimits.push(rule);
      }
      const newConfig = { ...config, limits: newLimits };
      const ok = await updateMemoryConfig(newConfig);
      if (ok) {
        showSuccess(
          editingIndex >= 0
            ? t('concurrency.messages.update_success')
            : t('concurrency.messages.create_success')
        );
        setModalOpen(false);
        setEditingIndex(-1);
      }
      return ok;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const newLimits = [...(config.limits || [])];
    newLimits.splice(index, 1);
    const newConfig = { ...config, limits: newLimits };
    const ok = await updateMemoryConfig(newConfig);
    if (ok) {
      showSuccess(t('concurrency.messages.delete_success'));
    }
  };

  const handleSaveToFile = async () => {
    setSaving(true);
    try {
      const res = await API.post('/api/concurrency/save', {
        config: JSON.stringify(config),
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || '并发配置已保存到集中配置');
      } else {
        showError(message || t('concurrency.messages.save_file_failed'));
      }
    } catch (error) {
      showError(error.message || t('concurrency.messages.save_file_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await API.get('/api/concurrency/export');
      downloadTextAsFile(
        typeof res.data === 'string'
          ? res.data
          : JSON.stringify(res.data, null, 2),
        'concurrency_config.json'
      );
      showSuccess('并发配置已下载');
    } catch (error) {
      showError(error.message || '导出并发配置失败');
    }
  };

  const handleExportToFile = async () => {
    setSaving(true);
    try {
      const res = await API.post('/api/concurrency/export-to-file');
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || '并发配置已导出到本地文件');
      } else {
        showError(message || '导出并发配置失败');
      }
    } catch (error) {
      showError(error.message || '导出并发配置失败');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setSaving(true);
    try {
      const res = await API.post('/api/concurrency/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(message || '并发配置导入成功');
        await loadConfig();
      } else {
        showError(message || '导入并发配置失败');
      }
    } catch (error) {
      showError(error.message || '导入并发配置失败');
    } finally {
      setFileInputKey((key) => key + 1);
      setSaving(false);
    }
  };

  const triggerImport = () => {
    fileInputRef.current?.click();
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      const res = await API.post('/api/concurrency/reload');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('concurrency.messages.reload_success'));
        await loadConfig();
      } else {
        showError(message || t('concurrency.messages.reload_failed'));
      }
    } catch (error) {
      showError(error.message || t('concurrency.messages.reload_failed'));
    } finally {
      setReloading(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const res = await API.post('/api/concurrency/restore');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('concurrency.messages.restore_success'));
        await loadConfig();
      } else {
        showError(message || t('concurrency.messages.restore_failed'));
      }
    } catch (error) {
      showError(error.message || t('concurrency.messages.restore_failed'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className='dashboard-container'>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Card fluid className='chart-card'>
              <Card.Content>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    flexWrap: 'wrap',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ minWidth: '280px', flex: '1 1 420px' }}>
                    <Card.Header className='header'>
                      {t('concurrency.title')}
                    </Card.Header>
                    <div
                      style={{
                        color: '#666',
                        fontSize: '13px',
                        lineHeight: 1.7,
                        marginTop: '8px',
                      }}
                    >
                      {t('concurrency.description')}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginTop: '12px',
                        alignItems: 'center',
                      }}
                    >
                      <Checkbox
                        toggle
                        checked={config.enabled}
                        onChange={handleToggleEnabled}
                        label={
                          config.enabled
                            ? t('concurrency.enabled')
                            : t('concurrency.disabled')
                        }
                      />
                      <Label basic color='blue'>
                        {t('concurrency.summary.total', {
                          count: summary.total,
                        })}
                      </Label>
                      <Label basic color='teal'>
                        P1: {summary.p1}
                      </Label>
                      <Label basic color='olive'>
                        P2: {summary.p2}
                      </Label>
                      <Label basic color='yellow'>
                        P3: {summary.p3}
                      </Label>
                      <Label basic color='grey'>
                        P4: {summary.p4}
                      </Label>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      flex: '1 1 320px',
                    }}
                  >
                    <Button
                      primary
                      style={{ minWidth: '108px' }}
                      onClick={openCreateModal}
                    >
                      {t('concurrency.buttons.add')}
                    </Button>
                    <Button
                      style={{ minWidth: '108px' }}
                      loading={saving}
                      onClick={handleSaveToFile}
                    >
                      保存到集中配置
                    </Button>
                    <Button
                      style={{ minWidth: '108px' }}
                      onClick={handleDownload}
                    >
                      下载 JSON
                    </Button>
                    <Button
                      style={{ minWidth: '132px' }}
                      loading={saving}
                      onClick={handleExportToFile}
                    >
                      导出到本地文件
                    </Button>
                    <Button
                      style={{ minWidth: '120px' }}
                      type='button'
                      onClick={triggerImport}
                    >
                      导入 JSON 文件
                    </Button>
                    <input
                      key={fileInputKey}
                      ref={fileInputRef}
                      type='file'
                      accept='.json,application/json'
                      onChange={handleImport}
                      style={{ display: 'none' }}
                    />
                    <Button
                      style={{ minWidth: '108px' }}
                      loading={reloading}
                      onClick={handleReload}
                    >
                      {t('concurrency.buttons.reload')}
                    </Button>
                    <Button
                      style={{ minWidth: '108px' }}
                      loading={restoring}
                      onClick={handleRestore}
                    >
                      {t('concurrency.buttons.restore')}
                    </Button>
                  </div>
                </div>
                <RulesTable
                  rules={config.limits || []}
                  loading={loading}
                  onEdit={openEditModal}
                  onDelete={handleDelete}
                />
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <EditRuleModal
        open={modalOpen}
        loading={saving}
        rule={editingIndex >= 0 ? config.limits[editingIndex] : null}
        onClose={closeModal}
        onSave={handleSave}
      />
    </div>
  );
};

export default ConcurrencyConfigPage;
